/**
 * Action: add_pastoral_note
 *
 * Append a confidential pastoral note to a person OR household.
 * Pairs with the focused-person / focused-household context (#1784):
 * on PersonProfile, "log a pastoral note: visited in hospital,
 * recovering well" lands with the right person_id.
 *
 * Why no inverse
 * --------------
 * Pastoral notes are append-only confidential records. A one-click
 * "undo" that deletes one would be a destructive surprise AND an
 * audit-trail integrity problem (the note may already have driven a
 * follow-up). Same rationale as create_task — return no inverse so
 * the UI doesn't offer undo. Correcting a note is an explicit edit
 * in the pastoral-care UI, not an action-engine undo.
 *
 * Encryption
 * ----------
 * `pastoral_notes.encrypted_note` is vault-encrypted. We MUST route
 * the plaintext through the `encrypt_pastoral_note` RPC (same path
 * as useCreatePastoralNote) — never write plaintext to the column.
 */

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { logAudit } from "@/lib/audit";
import { localDateYmd } from "@/lib/date-utils";
import { registerAction } from "../registry";
import type { ActionDefinition, ActionResult } from "../types";

const addPastoralNoteParams = z
  .object({
    person_id: z.string().guid().optional().nullable(),
    household_id: z.string().guid().optional().nullable(),
    /** Short non-confidential headline shown in the timeline list. */
    summary: z.string().trim().min(1).max(200),
    /** The confidential body — encrypted before storage. */
    note: z.string().trim().min(1).max(10000),
    /** ISO date (YYYY-MM-DD). Defaults to today when omitted. */
    note_date: z.string().min(1).optional().nullable(),
  })
  .refine((v) => Boolean(v.person_id) !== Boolean(v.household_id), {
    message: "Provide exactly one of person_id or household_id.",
  });

type AddPastoralNoteParams = z.infer<typeof addPastoralNoteParams>;

async function resolveSubjectName(
  organizationId: string,
  personId: string | null | undefined,
  householdId: string | null | undefined,
): Promise<string> {
  if (personId) {
    const { data, error } = await supabase
      .from("people")
      .select("first_name, last_name")
      .eq("organization_id", organizationId)
      .eq("id", personId)
      .maybeSingle();
    if (error || !data) {
      throw new Error("Person not found in this organization.");
    }
    const n = [data.first_name ?? "", data.last_name ?? ""]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(" ");
    return n || "this person";
  }
  if (householdId) {
    const { data, error } = await supabase
      .from("households")
      .select("name")
      .eq("organization_id", organizationId)
      .eq("id", householdId)
      .maybeSingle();
    if (error || !data) {
      throw new Error("Household not found in this organization.");
    }
    return (data.name ?? "").trim() || "this household";
  }
  // Unreachable — Zod refine guarantees one id — but keep total.
  throw new Error("Either a person or a household is required.");
}

function todayIso(): string {
  return localDateYmd();
}

const addPastoralNoteDefinition: ActionDefinition<AddPastoralNoteParams> = {
  id: "add_pastoral_note",
  label: "Add pastoral note",
  description:
    "Add a confidential pastoral-care note to a person or household.",
  requiredTier: "staff",
  paramsSchema: addPastoralNoteParams,
  // Confidential record with real downstream effects (timeline,
  // follow-up). Always confirm.
  defaultRequiresConfirm: true,
  async buildPreview(params, ctx) {
    const name = await resolveSubjectName(
      ctx.organizationId,
      params.person_id,
      params.household_id,
    );
    const date = params.note_date ?? todayIso();
    return {
      summary: `Add confidential pastoral note for ${name} (${date}): "${params.summary}"`,
    };
  },
  async execute(params, ctx): Promise<ActionResult> {
    let name: string;
    try {
      name = await resolveSubjectName(
        ctx.organizationId,
        params.person_id,
        params.household_id,
      );
    } catch (err) {
      return {
        ok: false,
        summary: "Could not load note subject",
        error: err instanceof Error ? err.message : String(err),
      };
    }

    // Encrypt the body via the vault-backed RPC. Never store
    // plaintext in encrypted_note.
    const { data: encrypted, error: encErr } = await supabase.rpc(
      "encrypt_pastoral_note",
      { note: params.note },
    );
    if (encErr || !encrypted) {
      return {
        ok: false,
        summary: `Failed to encrypt note for ${name}`,
        error: encErr?.message ?? "encryption returned empty",
      };
    }

    const { data, error } = await supabase
      .from("pastoral_notes")
      .insert({
        organization_id: ctx.organizationId,
        person_id: params.person_id ?? null,
        household_id: params.household_id ?? null,
        note_date: params.note_date ?? todayIso(),
        summary: params.summary,
        encrypted_note: encrypted as string,
        author_id: ctx.userId,
      })
      .select("id")
      .single();

    if (error || !data) {
      return {
        ok: false,
        summary: `Failed to save pastoral note for ${name}`,
        error: error?.message ?? "Unknown insert failure",
      };
    }

    // Audit records the SUMMARY only — never the confidential body.
    // The encrypted_note stays in pastoral_notes; copying plaintext
    // into audit_logs would defeat the encryption.
    const audit = await logAudit({
      action: "pastoral_note.add",
      tableName: "pastoral_notes",
      recordId: data.id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      newData: {
        person_id: params.person_id ?? null,
        household_id: params.household_id ?? null,
        subject_name: name,
        summary: params.summary,
        note_date: params.note_date ?? todayIso(),
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    return {
      ok: true,
      summary: `Added pastoral note for ${name}`,
      auditLogId: audit?.id ?? null,
      // No inverse — confidential append-only record (see header).
      inverse: null,
    };
  },
};

registerAction(addPastoralNoteDefinition);

export { addPastoralNoteDefinition };
